// src/utils/tempHPRule.js
//
// [SIM-TEMPHP-NO-STACKING-RULE — RULED BY JORDIE 2026-07-18: "5e take the higher"]
//
// THE ONE temp-HP resolution rule, extracted so every surface that RECEIVES temporary
// hit points resolves them identically. Before this file the product had TWO behaviours:
// gainTempHP (App.jsx — potions via drinkPotion) already took the higher, while the
// manual Temp HP input (VitalStatsCard) raw-ASSIGNED whatever was typed — so a player
// with 10 temp HP from Heroism who dutifully typed in their new False Life 8 was
// SILENTLY DOWNGRADED, the exact 12-vs-10 mistake the PHB example warns about.
//
// THE RULE (PHB 2014 p.198, verified against the local RAW text, "Temporary Hit Points"):
//   "they can't be added together. If you have temporary hit points and receive more of
//    them, you decide whether to keep the ones you have or to gain the new ones."
// RAW is a CHOICE; Jordie ruled the implementation as take-the-higher (the standard
// simplification, and what gainTempHP already did).
//
// WHY ENTRY HAS A ZERO-CLEARS BRANCH THAT GRANT DOES NOT: temp HP is also LOST outside
// of damage — "they last until they're depleted or you finish a long rest", and granting
// features carry durations that end early. Damage depletion and long rest are handled
// elsewhere (updateHP / RestPanel:641), but an effect ENDING EARLY has exactly one
// player-facing affordance: the manual field. Under a strict max() the field could never
// go down and expired temp HP would be immortal until the next long rest. So an entry of
// 0 is read as "my temp HP is gone" (removal — not a grant of zero), while any positive
// entry is read as receiving a pool (non-stacking applies). A grant of 0, by contrast,
// must NOT clear — max() handles that for free.
//
// MIRRORED (deliberately, by hand) in simulation/rules/hpRule.js:applyTempHPEntry so the
// shadow-model oracle predicts this exact behaviour; the parity test
// (src/__tests__/tempHPParity.test.js) runs both implementations over one vector table
// so they cannot drift apart silently. Do not change one without the other — the parity
// test is what fails if you do.

/**
 * Resolve RECEIVING temporary hit points (a grant from a spell/item/feature).
 * Non-stacking: the higher pool wins; grants can never lower or clear.
 *
 * @param {number} currentTemp - temp HP currently held (non-finite/negative → 0)
 * @param {number} granted     - the incoming pool (non-finite/negative → 0)
 * @returns {number} the resolved temp HP total
 */
export function resolveTempHPGrant(currentTemp, granted) {
  const cur = Number.isFinite(currentTemp) ? Math.max(0, currentTemp) : 0;
  const inc = Number.isFinite(granted) ? Math.max(0, granted) : 0;
  return Math.max(cur, inc);
}

/**
 * Resolve a MANUAL ENTRY in the Temp HP field.
 * 0 (or a cleared field) means "my temp HP is gone" — removal, not a grant of zero.
 * Any positive entry is a received pool: non-stacking take-the-higher.
 *
 * @param {number} currentTemp - temp HP currently held
 * @param {number} entered     - the committed field value (already clamped >= 0)
 * @returns {number} the resolved temp HP total
 */
export function resolveTempHPEntry(currentTemp, entered) {
  const inc = Number.isFinite(entered) ? entered : 0;
  if (inc <= 0) return 0;
  return resolveTempHPGrant(currentTemp, inc);
}
